import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, MapPin, User } from 'lucide-react';

const DaySchedule = ({ 
  entries = [], 
  day = 'Monday',
  userRole = 'student'
}) => {
  // Sort entries by start time
  const sortedEntries = React.useMemo(() => {
    return [...entries].sort((a, b) => {
      const timeA = a.startTime || a.time?.split(' - ')[0] || '';
      const timeB = b.startTime || b.time?.split(' - ')[0] || '';
      return timeA.localeCompare(timeB);
    });
  }, [entries]);

  // Color mapping for subjects
  const getSubjectColor = (subjectName) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-red-100 text-red-800 border-red-200',
    ];
    
    const hash = subjectName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    if (typeof time !== 'string') return String(time);
    return time.slice(0, 5); // Remove seconds if present
  };


  if (sortedEntries.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No classes scheduled</p>
            <p className="text-sm">for {day}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{day}'s Schedule</h3>
        <Badge variant="outline" className="text-sm">
          {sortedEntries.length} class{sortedEntries.length !== 1 ? 'es' : ''}
        </Badge>
      </div>

      <div className="space-y-3">
        {sortedEntries.map((entry, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`${getSubjectColor(entry.subjectName || entry.subject?.name || 'Unknown')}`}>
                      {entry.subjectName || entry.subject?.name || 'Unknown'}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {formatTime(entry.startTime || entry.time?.split(' - ')[0])} - {formatTime(entry.endTime || entry.time?.split(' - ')[1])}
                    </span>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">
                        {userRole === 'student' ? entry.teacherName : entry.className}
                      </span>
                    </div>
                    
                    {entry.room && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>Room {entry.room}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right text-sm text-gray-500">
                  <div className="font-medium">
                    {userRole === 'student' ? entry.className : entry.teacherName}
                  </div>
                  {entry.academicYear && (
                    <div className="text-xs">{entry.academicYear}</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DaySchedule;
